import './InviteBonus.sass';

import React, { FC, useMemo } from 'react';

import { useTranslator } from '$hooks';
import { classNames, format, formatCurrency, parse } from '$utils';
import { CurrencyEnum } from '$store/models';
import { InviteBonusProps } from '$core/Invite/InviteBonus/InviteBonus.interface';
import { Block } from '$uikit';

export const InviteBonus: FC<InviteBonusProps> = ({ bonus }) => {
  const t = useTranslator();

  const now = new Date();
  const startAt = parse(bonus.startAt, 'yyyy-MM-dd', now);
  const endAt = parse(bonus.endAt, 'yyyy-MM-dd', now);

  const isActive = useMemo(() => {
    if (startAt.getTime() > now.getTime()) {
      return false;
    }

    if (endAt.getTime() + 86400 * 1000 < now.getTime()) {
      return false;
    }

    return true;
  }, [bonus]);

  const maxLevel = useMemo(() => {
    let levels = bonus.levels;
    //levels.sort((a, b) => a.bonus - b.bonus ? 1 : -1);

    return levels[levels.length - 1];
  }, []);

  const totalAmount = useMemo(() => {
    return formatCurrency(4 * maxLevel.count + maxLevel.bonus, CurrencyEnum.EUR);
  }, [bonus, maxLevel]);

  const progress = useMemo(() => {
    const zones = bonus.levels.length;
    let total = 0;
    let prev = 0;
    for (let i = 0; i < zones; i++) {
      const invited = bonus.invitedCount - prev;
      const goal = bonus.levels[i].count - prev;
      const p = Math.min(100, (invited / goal) * 100);
      total += p / zones;
      prev += goal;
      if (p < 100) {
        break;
      }
    }

    return Math.floor(total);
  }, [maxLevel, bonus]);

  if (!isActive) {
    return null;
  }

  return (
    <Block className="InviteBonus">
      <div className="InviteBonus__title">
        {t('invite_bonus_title')} <span>{totalAmount}</span>
      </div>
      <div className="InviteBonus__caption">
        {t('invite_bonus_caption', {
          totalAmount: totalAmount,
        })}
      </div>
      <ul className="InviteBonus__options">
        <li>
          {t('invite_bonus_option1', {
            chunk1Bonuses: bonus.levels
              .slice(0, bonus.levels.length - 1)
              .map((level) => formatCurrency(level.bonus, CurrencyEnum.EUR))
              .join(', '),
            chunk2Bonuses: formatCurrency(maxLevel.bonus, CurrencyEnum.EUR),
            chunk1Counts: bonus.levels
              .slice(0, bonus.levels.length - 1)
              .map((level) => level.count)
              .join(', '),
            chunk2Counts: maxLevel.count,
          })}
        </li>
        <li>
          {t('invite_bonus_option2', { price: formatCurrency(4, CurrencyEnum.EUR) })}
        </li>
        <li>{t('invite_bonus_option3')}</li>
      </ul>
      <div className="InviteBonus__progress_wrap">
        <div className="InviteBonus__levels">
          {bonus.levels.map((level, i) => (
            <div
              className={classNames({
                InviteBonus__level: true,
                active:
                  bonus.invitedCount >= level.count &&
                  (i === bonus.levels.length - 1 ||
                    bonus.invitedCount < bonus.levels[i + 1].count),
              })}
              key={level.count}
            >
              {formatCurrency(level.bonus, CurrencyEnum.EUR)}
            </div>
          ))}
        </div>
        <div className="InviteBonus__progress">
          <div
            className="InviteBonus__progress__indicator"
            style={{ width: `${progress}%` }}
          />
          <div className="InviteBonus__progress__levels">
            {bonus.levels.map((level) => (
              <div className="InviteBonus__progress__level" key={level.count}>
                <div
                  className={classNames({
                    InviteBonus__progress__level__indicator: true,
                    active: bonus.invitedCount >= level.count,
                  })}
                />
              </div>
            ))}
          </div>
        </div>
        <div
          className={classNames({
            InviteBonus__progress_title: true,
            hasInvited: bonus.invitedCount > 0,
          })}
        >
          {t('invite_bonus_invited', {
            count: bonus.invitedCount,
          })}
        </div>
      </div>
      <div className="InviteBonus__info">
        <div className="InviteBonus__info__dates">
          {t('invite_bonus_info', {
            start: format(startAt, 'd MMM'),
            end: format(endAt, 'd MMM'),
          })}
        </div>
        <a
          href="https://wiki.ton.place/docs-and-terms/akciya-referalnye-bonusy-3"
          target="_blank"
          className="InviteBonus__info__details"
          rel="noreferrer"
        >
          {t('invite_bonus_detaild')}
        </a>
      </div>
    </Block>
  );
};
